import { useEffect, useRef, useState } from "react";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { VISITS_PER_MONTH } from "../lib/pricing";
import { useIsMobile } from "../lib/useIsMobile";
import { useBackClose } from "../lib/navStack";

// ═══════════════════════════════════════════════════════════════════
// SETTINGS → WEBSITE  — the operator's website "studio" (CRM side)
// ═══════════════════════════════════════════════════════════════════
// A 2-pane studio bound to the operator's ONE `sites` row (created on
// first open, PREFILLED from the CRM — pricing from service_prices,
// areas from service_area, business info from the org). Left = a guided
// wizard; right = a LIVE preview that assembles as you type. The same
// row drives the public shared template (separate Next.js renderer).
// The operator never sees a page builder — this IS the editor.
// ═══════════════════════════════════════════════════════════════════

const EDITABLE = ["subdomain","business_name","tagline","phone","email","guarantee_text",
  "service_areas","services","pricing_plans","reviews","logo_url","hero_image_url","gallery","theme","seo","published"];

const STEPS = [
  { short:"Business", title:"Business basics",   blurb:"The front door of your business." },
  { short:"Coverage", title:"Areas & services",  blurb:"Pulled live from your CRM — nothing to enter." },
  { short:"Pricing",  title:"Pricing & reviews", blurb:"Pricing comes from your CRM. Add a few reviews." },
  { short:"Brand",    title:"Branding & photos", blurb:"Make it unmistakably yours." },
  { short:"Launch",   title:"Launch",            blurb:"Claim your address and go live." },
];
const EMBED_HOST = "cryptohunting.world"; // the public renderer host (Vercel-deployed)
const DEFAULT_THEME = { accent:"#f2622e", base:"#1f9d55", radius:"16px" };
const ACCENT_PRESETS = ["#f2622e","#0071E3","#1A8A52","#7A56E7","#E0457B","#0E7C86"];
// Hand-tuned accent+base PAIRS. Each pair is harmonized (analogous or color+neutral)
// and dark enough that white button/band text always clears WCAG AA — so an operator
// can't accidentally build a garish or unreadable site. Custom hex stays available.
const THEME_PRESETS = [
  { name: "Evergreen", accent: "#15803d", base: "#0f766e" }, // green + teal
  { name: "Ocean",     accent: "#0369a1", base: "#0e7490" }, // blue + cyan
  { name: "Sunset",    accent: "#c2410c", base: "#9a3412" }, // orange + rust
  { name: "Royal",     accent: "#6d28d9", base: "#4338ca" }, // violet + indigo
  { name: "Slate",     accent: "#1d4ed8", base: "#334155" }, // blue + slate
  { name: "Berry",     accent: "#be185d", base: "#9d174d" }, // pink + berry
];
const SERVICE_LABELS = {
  cleanup: { name:"Dog waste cleanup",    desc:"Regular yard scooping, every visit" },
  deo:     { name:"Deodorizer treatment", desc:"Yard deodorizing add-on" },
};
const PLAN_ORDER = [["biweekly","Bi-weekly"],["weekly","Weekly"],["twice-weekly","Twice weekly"]];

const slugify = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 32);
const authHeaders = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });

// Downscale + WebP an uploaded image so hosted sites stay fast.
async function toWebp(file, maxDim = 1600, quality = 0.85) {
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return file;
  const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
  return await new Promise((res) => canvas.toBlob((b) => res(b || file), "image/webp", quality));
}

// ── Build the prefill seed from what the CRM already knows ───────────
// Best-effort: every fetch is guarded so a slow/failed read never blocks
// the brand-new site row from being created. Runs ONLY on first create.
async function buildPrefill(org, ownerEmail, clients) {
  const seed = {
    business_name: org?.name || "",
    phone: org?.phone || "",
    email: ownerEmail || "",
    tagline: "A spotless yard, every week — without lifting a finger.",
    guarantee_text: "Happy or it's free.",
    theme: { ...DEFAULT_THEME },
  };
  const H = authHeaders();

  // pricing_plans + services  ←  service_prices (the operator's rate card)
  try {
    const rows = await fetch(`${SUPABASE_URL}/rest/v1/service_prices?select=service,frequency,dogs,price`, { headers: H }).then(r => r.json());
    if (Array.isArray(rows)) {
      const m = {}; let hasDeo = false;
      rows.forEach((r) => {
        if (r.service === "cleanup") { (m[r.frequency] = m[r.frequency] || {})[r.dogs] = Number(r.price); }
        else if (r.service === "deo") { hasDeo = true; }
      });
      // "most popular" ← the modal active-client cadence (real demand), else weekly
      let popular = "weekly";
      if (Array.isArray(clients) && clients.length) {
        const cnt = {};
        clients.forEach((c) => { if (c.active !== false && c.frequency) cnt[c.frequency] = (cnt[c.frequency] || 0) + 1; });
        popular = Object.entries(cnt).sort((a, b) => b[1] - a[1])[0]?.[0] || "weekly";
      }
      const plans = [];
      PLAN_ORDER.forEach(([freq, name]) => {
        const monthly = m[freq]?.[1]; // 1-dog monthly headline
        if (monthly != null) {
          const perVisit = Math.round(monthly / (VISITS_PER_MONTH[freq] || 4.33));
          plans.push({ name, price: perVisit, monthly: Math.round(monthly), popular: freq === popular });
        }
      });
      if (plans.length && !plans.some((p) => p.popular)) (plans.find((p) => p.name === "Weekly") || plans[Math.floor(plans.length / 2)]).popular = true;
      if (plans.length) seed.pricing_plans = plans;
      seed.services = [SERVICE_LABELS.cleanup, ...(hasDeo ? [SERVICE_LABELS.deo] : [])].map((s) => ({ name: s.name, desc: s.desc }));
    }
  } catch { /* keep going — pricing stays empty for the owner to fill */ }

  // service_areas (zips, town backfilled from clients.city)  ←  service_area
  try {
    const [areas, cityRows] = await Promise.all([
      fetch(`${SUPABASE_URL}/rest/v1/service_area?select=zip,day`, { headers: H }).then(r => r.json()).catch(() => []),
      fetch(`${SUPABASE_URL}/rest/v1/clients?select=zip,city&city=not.is.null`, { headers: H }).then(r => r.json()).catch(() => []),
    ]);
    const zipCity = {};
    if (Array.isArray(cityRows)) cityRows.forEach((c) => { if (c.zip && c.city && !zipCity[c.zip]) zipCity[c.zip] = c.city; });
    if (Array.isArray(areas) && areas.length) {
      const seen = new Set(), list = [];
      areas.forEach((a) => { const z = String(a.zip || "").trim(); if (z && !seen.has(z)) { seen.add(z); list.push({ town: zipCity[z] || "", zip: z }); } });
      if (list.length) seed.service_areas = list;
    }
  } catch { /* areas stay empty */ }

  const town = seed.service_areas?.[0]?.town;
  seed.seo = { title: `${seed.business_name || "Your business"} — Dog waste removal in ${town || "your area"}` };
  return seed;
}

// ── Live CRM content for the website ─────────────────────────────────
// Pricing (service_prices), service areas (service_area) and services are OWNED by the
// CRM — the operator manages them in Settings → Pricing / Service Areas. The website PULLS
// them live, so the wizard shows them read-only instead of asking for them again.
async function loadCrmContent(clients) {
  const H = authHeaders();
  const out = { plans: [], areas: [], services: [] };
  try {
    const rows = await fetch(`${SUPABASE_URL}/rest/v1/service_prices?select=service,frequency,dogs,price`, { headers: H }).then(r => r.json());
    if (Array.isArray(rows)) {
      const m = {}; let hasDeo = false;
      rows.forEach((r) => { if (r.service === "cleanup") { (m[r.frequency] = m[r.frequency] || {})[r.dogs] = Number(r.price); } else if (r.service === "deo") hasDeo = true; });
      let popular = "weekly";
      if (Array.isArray(clients) && clients.length) {
        const c = {}; clients.forEach((x) => { if (x.active !== false && x.frequency) c[x.frequency] = (c[x.frequency] || 0) + 1; });
        popular = Object.entries(c).sort((a, b) => b[1] - a[1])[0]?.[0] || "weekly";
      }
      PLAN_ORDER.forEach(([f, name]) => { const mo = m[f]?.[1]; if (mo != null) out.plans.push({ name, price: Math.round(mo / (VISITS_PER_MONTH[f] || 4.33)), monthly: Math.round(mo), popular: f === popular }); });
      if (out.plans.length && !out.plans.some((p) => p.popular)) (out.plans.find((p) => p.name === "Weekly") || out.plans[0]).popular = true;
      out.services = [SERVICE_LABELS.cleanup, ...(hasDeo ? [SERVICE_LABELS.deo] : [])].map((s) => ({ name: s.name, desc: s.desc }));
    }
  } catch { /* leave empty */ }
  try {
    const areas = await fetch(`${SUPABASE_URL}/rest/v1/service_area?select=zip,day`, { headers: H }).then(r => r.json()).catch(() => []);
    const uniqueZips = [...new Set((Array.isArray(areas) ? areas : []).map((a) => String(a.zip || "").trim()).filter(Boolean))];
    // Resolve EVERY zip → city via the clean Zippopotam source, then group by city so a
    // city with several zips shows once (matches the live site exactly).
    const resolved = await Promise.all(uniqueZips.map(async (z) => {
      try { const r = await fetch(`https://api.zippopotam.us/us/${encodeURIComponent(z)}`); if (!r.ok) return { zip: z, city: "" }; const d = await r.json(); return { zip: z, city: d?.places?.[0]?.["place name"] || "" }; }
      catch { return { zip: z, city: "" }; }
    }));
    const seen = new Set();
    resolved.forEach(({ zip, city }) => {
      if (city) { const k = city.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.areas.push({ town: city, zip }); } }
      else out.areas.push({ town: "", zip });
    });
  } catch { /* leave empty */ }
  return out;
}

export function WebsiteSettings({ org, ownerEmail, clients, showToast }) {
  const [site,      setSite]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [step,      setStep]      = useState(0);
  const [error,     setError]     = useState("");
  const [savedJSON, setSavedJSON] = useState("");
  const [subStatus, setSubStatus] = useState("idle"); // idle|checking|available|taken|invalid|checkfailed
  const [busySlot,  setBusySlot]  = useState(null);
  const [prefilled, setPrefilled] = useState(false);   // show the "from your CRM" assist
  const [editing,   setEditing]   = useState(false);   // re-editing an already-published site
  const [editSnap,  setEditSnap]  = useState(null);    // pre-edit snapshot, restored on Cancel
  const [crm, setCrm] = useState({ plans: [], areas: [], services: [] }); // live CRM content (pricing/areas/services)
  const subTimer  = useRef(null);
  const saveTimer = useRef(null);
  const savingRef = useRef(false); // synchronous in-flight guard (state `saving` is async)
  // Phones: the sticky right-hand preview can't sit beside the form, so it becomes a
  // bottom sheet opened from a "Preview" button (iOS back gesture closes it).
  const isMobile = useIsMobile();
  const [pvOpen, setPvOpen] = useState(false);
  useBackClose(pvOpen && isMobile, () => setPvOpen(false));

  const snap = (row) => JSON.stringify(EDITABLE.reduce((o, k) => { o[k] = row?.[k]; return o; }, {}));
  let parsedSaved = {}; try { parsedSaved = JSON.parse(savedJSON || "{}"); } catch { parsedSaved = {}; }
  const fieldDirty = (k) => JSON.stringify(site?.[k]) !== JSON.stringify(parsedSaved[k]);
  const subSettled = subStatus === "available" || subStatus === "idle";
  const otherDirty = !!site && EDITABLE.some((k) => k !== "subdomain" && fieldDirty(k));
  const subDirty   = !!site && fieldDirty("subdomain");
  const dirty        = otherDirty || subDirty;              // for display
  const savableDirty = otherDirty || (subDirty && subSettled); // safe to PATCH right now

  // ── load (or create + PREFILL) the org's single site row ──────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await fetch(`${SUPABASE_URL}/rest/v1/sites?select=*&limit=1`, { headers: authHeaders() }).then(r => r.json());
        let row = Array.isArray(rows) ? rows[0] : null;
        let didPrefill = false;
        if (!row) {
          const seed = await buildPrefill(org, ownerEmail, clients);
          didPrefill = true;
          const res = await fetch(`${SUPABASE_URL}/rest/v1/sites`, {
            method: "POST",
            headers: { ...authHeaders(), "Content-Type": "application/json", Prefer: "return=representation" },
            body: JSON.stringify(seed),
          });
          if (res.ok) { const c = await res.json(); row = Array.isArray(c) ? c[0] : c; }
          else {
            // a concurrent open (two tabs / StrictMode double-effect) won the
            // one-site-per-org unique race → load the row it created (no prefill).
            didPrefill = false;
            const again = await fetch(`${SUPABASE_URL}/rest/v1/sites?select=*&limit=1`, { headers: authHeaders() }).then(r => r.json());
            row = Array.isArray(again) ? again[0] : null;
          }
        }
        if (!cancelled && row && row.id) {
          setSite(row); setSavedJSON(snap(row)); setPrefilled(didPrefill);
          loadCrmContent(clients).then((c) => { if (!cancelled) setCrm(c); }); // pull live pricing/areas/services
        }
        else if (!cancelled) setError("Could not load your website settings.");
      } catch (e) { if (!cancelled) setError("Could not load your website settings."); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function set(patch)      { setSite((s) => ({ ...s, ...patch })); }
  function setTheme(patch) { setSite((s) => ({ ...s, theme: { ...(s.theme || {}), ...patch } })); }
  function setSeo(patch)   { setSite((s) => ({ ...s, seo:   { ...(s.seo   || {}), ...patch } })); }
  function addRow(key, blank)     { setSite((s) => ({ ...s, [key]: [...(s[key] || []), blank] })); }
  function setRow(key, i, patch)  { setSite((s) => ({ ...s, [key]: (s[key] || []).map((r, j) => j === i ? { ...r, ...patch } : r) })); }
  function delRow(key, i)         { setSite((s) => ({ ...s, [key]: (s[key] || []).filter((_, j) => j !== i) })); }

  async function save(extra) {
    if (!site?.id || savingRef.current) return null;
    savingRef.current = true; setSaving(true); setError("");
    const ex = extra || {};
    const body = EDITABLE.reduce((o, k) => { o[k] = site[k]; return o; }, {});
    Object.assign(body, ex);
    // Defer ONLY the subdomain while it isn't settled (checking/taken/invalid/checkfailed)
    // so every OTHER field still persists. Send the last-saved subdomain so the PATCH never
    // trips 23505 or wipes a good value; keep the operator's in-progress text in local state.
    const deferSub = !("subdomain" in ex) && fieldDirty("subdomain") && !(subStatus === "available" || subStatus === "idle");
    if (deferSub) body.subdomain = parsedSaved.subdomain ?? null;
    try {
      const res = await fetch(`${SUPABASE_URL}/rest/v1/sites?id=eq.${site.id}`, {
        method: "PATCH",
        headers: { ...authHeaders(), "Content-Type": "application/json", Prefer: "return=representation" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        if ((e.code === "23505") || /duplicate|unique/i.test(e.message || "")) { setError("That subdomain is taken — pick another."); return null; }
        setError(e.message || `Save failed (${res.status})`); return null;
      }
      const rows = await res.json();
      const row = Array.isArray(rows) ? rows[0] : rows;
      const merged = deferSub ? { ...row, subdomain: site.subdomain } : row; // keep in-progress subdomain
      setSite(merged); setSavedJSON(snap(row));                              // savedJSON = persisted truth
      return row;
    } catch (e) { setError("Save failed — check connection"); return null; }
    finally { savingRef.current = false; setSaving(false); }
  }

  // ── autosave (debounced) — quietly persists as you work. Only fires when there's a
  // SAVABLE change (a non-subdomain edit, or a subdomain that's settled), so an unsettled
  // subdomain never blocks other fields and never churns a no-op PATCH. ──
  useEffect(() => {
    if (!site?.id || !savableDirty || saving) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { save(); }, 1400);
    return () => clearTimeout(saveTimer.current);
  }, [site, savableDirty, saving]); // eslint-disable-line react-hooks/exhaustive-deps

  // subdomain availability (debounced; RPC sees across orgs)
  function onSubdomain(v) {
    const sub = slugify(v);
    set({ subdomain: sub || null });
    clearTimeout(subTimer.current);
    if (!sub) { setSubStatus("idle"); return; }            // cleared → a valid "remove address"; autosave persists null
    if (sub.length < 3) { setSubStatus("invalid"); return; }
    setSubStatus("checking");
    subTimer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${SUPABASE_URL}/rest/v1/rpc/subdomain_available`, {
          method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
          body: JSON.stringify({ p_sub: sub }),
        });
        const ok = await r.json();
        setSubStatus(ok === true ? "available" : "taken");
      } catch (e) { setSubStatus("checkfailed"); }
    }, 400);
  }

  async function uploadImage(file, slot) {
    if (!file || !site?.id) return;
    if (!file.type || !file.type.startsWith("image/")) { setError("Please choose an image file."); return; }
    if (file.size > 10 * 1024 * 1024) { setError("Image too large — please use one under 10 MB."); return; }
    clearTimeout(saveTimer.current); // don't let a queued autosave race this upload's save()
    setBusySlot(slot); setError("");
    try {
      const blob = await toWebp(file);
      const path = `${site.id}/${slot}-${Date.now()}.webp`;
      const res = await fetch(`${SUPABASE_URL}/storage/v1/object/site-assets/${path}`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "image/webp", "x-upsert": "true" },
        body: blob,
      });
      if (!res.ok) { setError("Image upload failed."); setBusySlot(null); return; }
      const url = `${SUPABASE_URL}/storage/v1/object/public/site-assets/${path}`;
      if (slot === "logo") await save({ logo_url: url });
      else if (slot === "hero") await save({ hero_image_url: url });
      else await save({ gallery: [...(site.gallery || []), url] });
    } catch (e) { setError("Image upload failed."); }
    finally { setBusySlot(null); }
  }

  if (loading) return <div className="card"><div style={{ padding: 24, color: "var(--text2)" }}>Building your studio…</div></div>;
  if (!site)   return <div className="card"><div style={{ padding: 24, color: "var(--danger)" }}>{error || "Could not load website settings."}</div></div>;

  const crmAreas = crm.areas || [];   // service areas — live from the CRM (read-only here)
  const crmPlans = crm.plans || [];   // pricing — live from the CRM (read-only here)
  const crmServices = crm.services || [];
  const canPublish = site.business_name && site.phone && site.email && crmAreas.length > 0 &&
    (site.subdomain && (subStatus === "available" || subStatus === "idle"));
  const liveUrl = site.subdomain ? `https://${site.subdomain}.${EMBED_HOST}` : null;
  const accent  = site.theme?.accent || DEFAULT_THEME.accent;
  const managing = !!site.published && !editing;   // published & not actively editing → manage view

  const stepDone = (i) =>
    i === 0 ? !!(site.business_name && site.phone && site.email) :
    i === 1 ? crmAreas.length > 0 :
    i === 2 ? crmPlans.length > 0 :
    i === 3 ? !!site.theme?.accent :
    i === 4 ? !!site.published : false;

  const copy = (text, ok) => {
    if (navigator.clipboard) navigator.clipboard.writeText(text).then(() => showToast && showToast(ok)).catch(() => showToast && showToast("Couldn't copy — select it manually", "error"));
    else showToast && showToast("Select it manually to copy", "error");
  };

  async function doPublish() {
    const wasLive = !!site.published;
    const r = await save({ published: true });
    if (r) {
      fetch(`${SUPABASE_URL}/functions/v1/revalidate-site`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ site_id: r.id }),
      }).catch(() => {});
      setEditing(false); setEditSnap(null);            // leave edit mode → manage view
      showToast && showToast(wasLive ? "Changes published — your site is updated" : "🎉 Your website is live!");
    }
  }

  // Enter edit mode, snapshotting current content so Cancel can fully revert.
  function enterEdit() {
    setEditSnap(JSON.parse(JSON.stringify(site)));
    setStep(0);
    setEditing(true);
  }
  // Cancel: discard this editing session by restoring the snapshot (and persisting it).
  async function cancelEdit() {
    if (editSnap) {
      const restore = EDITABLE.reduce((o, k) => { o[k] = editSnap[k]; return o; }, {});
      setSite((s) => ({ ...s, ...restore }));
      await save(restore);
    }
    setEditSnap(null);
    setEditing(false);
  }

  const CrmChip = () => prefilled
    ? <span className="ws-crm" title="Filled in from your CRM">✓ from your CRM</span> : null;

  // Published & not editing → the calm single-column "manage" view (no wizard).
  if (managing) {
    return (
      <div className="ws-managewrap">
        <style>{WS_CSS}</style>
        <ManageView site={site} crm={crm} liveUrl={liveUrl} copy={copy} onEdit={enterEdit} />
      </div>
    );
  }

  return (
    <div className="ws-studio">
      <style>{WS_CSS}</style>

      {/* ════════ LEFT — the wizard ════════ */}
      <div className="ws-left">
        <div className="ws-head">
          <div>
            <div className="ws-title">{site.published ? "Editing your website" : "Website studio"}</div>
            <div className="ws-sub">{site.published ? "Make your changes, then Republish to push them live." : isMobile ? "Fill this out once — it builds your hosted marketing site from one shared template. Tap Preview any time to see it come together." : "Fill this out once — it builds your hosted marketing site from one shared template. Watch it come together on the right. →"}</div>
          </div>
          <div className="ws-headright">
            <span className={`ws-status ${site.published ? "live" : "draft"}`}>{site.published ? "● Live" : "○ Draft"}</span>
            {isMobile && <button type="button" className="btn btn-ghost btn-sm ws-pvbtn" onClick={() => setPvOpen(true)}>👁 Preview</button>}
          </div>
        </div>

        {/* stepper */}
        <div className="steps ws-steps">
          {STEPS.map((s, i) => (
            <button key={i} className="step ws-stepbtn" onClick={() => setStep(i)} type="button">
              <span className={`step-dot${i === step ? " active" : stepDone(i) ? " done" : ""}`}>{stepDone(i) && i !== step ? "✓" : i + 1}</span>
              <span className={`step-label${i === step ? " active" : ""}`}>{s.short}</span>
            </button>
          ))}
        </div>

        <div className="ws-form">
          <div className="ws-stephead">
            <div className="ws-steptitle">{STEPS[step].title}</div>
            <div className="ws-stepblurb">{STEPS[step].blurb}</div>
          </div>

          {/* ── STEP 1 — business ── */}
          {step === 0 && (<>
            <Field label={<>Business name * <CrmChip/></>}>
              <input value={site.business_name || ""} onChange={e => set({ business_name: e.target.value })} placeholder="Scoopers on Doody" /></Field>
            <Field label="Headline (the big promise)">
              <input value={site.tagline || ""} onChange={e => set({ tagline: e.target.value })} placeholder="A spotless yard, every week — without lifting a finger." /></Field>
            <div className="grid-2">
              <Field label={<>Phone * <CrmChip/></>}>
                <input value={site.phone || ""} onChange={e => set({ phone: e.target.value })} placeholder="(555) 123-4567" /></Field>
              <Field label={<>Email * <CrmChip/></>}>
                <input value={site.email || ""} onChange={e => set({ email: e.target.value })} placeholder="hello@yourbiz.com" /></Field>
            </div>
            <Field label="Guarantee line (your risk-reversal)">
              <input value={site.guarantee_text || ""} onChange={e => set({ guarantee_text: e.target.value })} placeholder="Happy or it's free." /></Field>
          </>)}

          {/* ── STEP 2 — service areas & services (LIVE from the CRM, read-only) ── */}
          {step === 1 && (<>
            <div className="ws-pulled">✓ Pulled live from your CRM — you manage these in <b>Settings → Service Areas</b> and <b>Pricing</b>. No need to re-enter them.</div>
            <div className="ws-sectitle">Service areas</div>
            {crmAreas.length === 0
              ? <div className="ws-roempty">No service areas yet — add your zip codes in <b>Settings → Service Areas</b> and they'll show up on your site automatically.</div>
              : <div className="ws-chips">{crmAreas.map((a, i) => <span key={i} className="ws-areachip">{a.town || a.zip}</span>)}</div>}
            <div className="ws-sectitle" style={{ marginTop: 18 }}>Services</div>
            {crmServices.length === 0
              ? <div className="ws-roempty">Set your prices in <b>Settings → Pricing</b> and your services will be listed here.</div>
              : <div className="ws-rolist">{crmServices.map((s, i) => <div key={i} className="ws-roitem"><b>{s.name}</b>{s.desc ? <span> — {s.desc}</span> : null}</div>)}</div>}
          </>)}

          {/* ── STEP 3 — pricing (LIVE from the CRM) + reviews ── */}
          {step === 2 && (<>
            <div className="ws-pulled">✓ Pricing is pulled live from your CRM — edit it in <b>Settings → Pricing</b> and it updates on your site automatically.</div>
            <div className="ws-sectitle">Your plans</div>
            {crmPlans.length === 0
              ? <div className="ws-roempty">No pricing set yet — add it in <b>Settings → Pricing</b>.</div>
              : <div className="ws-plansro">{crmPlans.map((p, i) => (
                  <div key={i} className={`ws-planro${p.popular ? " pop" : ""}`}>
                    {p.popular && <span className="ws-planro-badge">Most popular</span>}
                    <div className="ws-planro-name">{p.name}</div>
                    <div className="ws-planro-price">${p.price}<small>/visit</small></div>
                    {p.monthly ? <div className="ws-planro-mo">about ${p.monthly}/mo</div> : null}
                  </div>
                ))}</div>}

            <div className="ws-secrow" style={{ marginTop: 22 }}><div className="ws-sectitle">Reviews <span className="ws-opt">optional</span></div></div>
            <div className="ws-hint">We can't import these — paste a few real ones from Google or customer texts. Real names + towns convert best.</div>
            {(site.reviews || []).map((r, i) => (
              <div key={i} className="ws-rowcard">
                <div className="ws-row" style={{ flexWrap: "wrap", marginBottom: 6 }}>
                  <input style={{ flex: 1, minWidth: 110 }} value={r.name || ""} onChange={e => setRow("reviews", i, { name: e.target.value })} placeholder="Name" />
                  <input style={{ flex: 1, minWidth: 90 }} value={r.town || ""} onChange={e => setRow("reviews", i, { town: e.target.value })} placeholder="Town" />
                  <Stars value={r.stars || 5} onChange={n => setRow("reviews", i, { stars: n })} />
                  <button className="ws-x" onClick={() => delRow("reviews", i)} title="Remove">✕</button>
                </div>
                <textarea value={r.text || ""} onChange={e => setRow("reviews", i, { text: e.target.value })} placeholder="What they said…" style={{ minHeight: 50 }} />
              </div>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={() => addRow("reviews", { name: "", town: "", stars: 5, text: "" })}>+ Add review</button>
          </>)}

          {/* ── STEP 4 — branding & photos ── */}
          {step === 3 && (<>
            <div className="ws-sectitle">Theme</div>
            <div className="ws-stepblurb" style={{ marginTop: -2, marginBottom: 11 }}>Pick a ready-made color theme — each pair is hand-tuned to look clean and stay easy to read.</div>
            <div className="ws-palette">
              {THEME_PRESETS.map(p => {
                const on = (site.theme?.accent || "").toLowerCase() === p.accent && (site.theme?.base || "").toLowerCase() === p.base;
                return (
                  <button key={p.name} type="button" className={`ws-pal${on ? " on" : ""}`} aria-pressed={on}
                    onClick={() => setTheme({ accent: p.accent, base: p.base })}>
                    <span className="ws-pal-chips"><span style={{ background: p.accent }} /><span style={{ background: p.base }} /></span>
                    <span className="ws-pal-name">{p.name}</span>
                    {on && <span className="ws-pal-check">✓</span>}
                  </button>
                );
              })}
            </div>
            <details className="ws-custom">
              <summary>Custom colors</summary>
              <div style={{ marginTop: 12 }}>
                <Swatch label="Accent (buttons & highlights)" value={accent} onChange={v => setTheme({ accent: v })} warn />
                <Swatch label="Base (headers & trust strip)" value={site.theme?.base || DEFAULT_THEME.base} onChange={v => setTheme({ base: v })} />
              </div>
            </details>

            <div className="ws-sectitle" style={{ marginTop: 18 }}>Photos</div>
            <div className="grid-2">
              <ImageSlot label="Logo" url={site.logo_url} busy={busySlot === "logo"} onFile={f => uploadImage(f, "logo")} onClear={() => save({ logo_url: null })} />
              <ImageSlot label="Hero image" url={site.hero_image_url} busy={busySlot === "hero"} onFile={f => uploadImage(f, "hero")} onClear={() => save({ hero_image_url: null })} />
            </div>
            <div className="ws-fieldlabel" style={{ marginTop: 14 }}>Gallery</div>
            <div className="ws-gallery">
              {(site.gallery || []).map((u, i) => (
                <div key={i} className="ws-gitem">
                  <img className="ws-img" src={u} alt="" style={{ height: 90 }} />
                  <button className="ws-x ws-x-float" onClick={() => save({ gallery: (site.gallery || []).filter((_, j) => j !== i) })}>✕</button>
                </div>
              ))}
              <label className="ws-drop" style={{ height: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {busySlot === "gallery" ? "Uploading…" : "+ Add photo"}
                <input type="file" accept="image/*" hidden onChange={e => e.target.files[0] && uploadImage(e.target.files[0], "gallery")} />
              </label>
            </div>
            <div className="ws-note">AI photo generation is coming once the hosted image backend is wired up.</div>
          </>)}

          {/* ── STEP 5 — launch ── */}
          {step === 4 && (<>
            <div className="ws-sectitle">Your web address</div>
            <div className="ws-fieldlabel">Subdomain</div>
            <div className="ws-addr">
              <input value={site.subdomain || ""} onChange={e => onSubdomain(e.target.value)} placeholder="happytails" />
              <span className="ws-addrhost">.{EMBED_HOST}</span>
            </div>
            <div className={`ws-substatus ${subStatus}`}>
              {subStatus === "checking" ? "Checking…" :
               subStatus === "available" ? <><b>✓ Available</b> — {site.subdomain}.{EMBED_HOST} is yours</> :
               subStatus === "taken" ? "Taken — try another" :
               subStatus === "invalid" ? "3–32 chars: letters, numbers, hyphens" :
               subStatus === "checkfailed" ? "Couldn't verify — try again" :
               site.subdomain ? <>Your address: <b>{site.subdomain}.{EMBED_HOST}</b></> : "Pick a short, memorable address"}
            </div>

            <div className="ws-sectitle" style={{ marginTop: 18 }}>Search listing</div>
            <Field label="Page title">
              <input value={site.seo?.title || ""} onChange={e => setSeo({ title: e.target.value })} placeholder={`${site.business_name || "Your business"} — Dog waste removal in ${crmAreas[0]?.town || "your area"}`} /></Field>
            <Field label="Meta description">
              <textarea value={site.seo?.description || ""} onChange={e => setSeo({ description: e.target.value })} placeholder="One or two sentences Google shows under your title." style={{ minHeight: 54 }} /></Field>

            {/* checklist — click to jump */}
            <div className="ws-checklist">
              <div className="ws-cltitle">Before you go live</div>
              <ChecklistItem ok={!!site.business_name} label="Business name" onClick={() => setStep(0)} />
              <ChecklistItem ok={!!site.phone} label="Phone" onClick={() => setStep(0)} />
              <ChecklistItem ok={!!site.email} label="Email" onClick={() => setStep(0)} />
              <ChecklistItem ok={crmAreas.length > 0} label="Service area (from your CRM)" onClick={() => setStep(1)} />
              <ChecklistItem ok={!!site.subdomain && subStatus !== "taken" && subStatus !== "invalid"} label="Web address" onClick={() => {}} />
            </div>

            {site.published && liveUrl && (
              <div className="ws-liverow">Live at <a href={liveUrl} target="_blank" rel="noreferrer">{liveUrl}</a></div>
            )}

            <div className="ws-sectitle" style={{ marginTop: 18 }}>Already have a website?</div>
            <div className="ws-note" style={{ marginBottom: 6 }}>Drop your quote form into any site — leads flow straight into your CRM.</div>
            <pre className="ws-embed">{embedSnippet(site.id)}</pre>
            <button className="btn btn-ghost btn-sm" onClick={() => copy(embedSnippet(site.id), "Embed snippet copied")}>📋 Copy snippet</button>
          </>)}

          {error && <div className="ws-err">⚠ {error}</div>}

          {/* footer nav */}
          <div className="ws-foot">
            {step > 0 && <button className="btn btn-ghost btn-sm" onClick={() => setStep(step - 1)}>← Back</button>}
            <span className={`ws-savestate${savableDirty || saving ? " busy" : ""}`}>{saving || savableDirty ? "Saving…" : subDirty ? "Finish your web address to save it" : "Saved ✓"}</span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
              {isMobile && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPvOpen(true)}>👁 Preview</button>}
              {site.published && <button className="btn btn-ghost btn-sm" disabled={saving} onClick={cancelEdit}>Cancel</button>}
              {step < STEPS.length - 1
                ? <button className="btn btn-gold btn-sm" disabled={saving} onClick={async () => { const ok = dirty ? await save() : true; if (ok) setStep(step + 1); }}>Next →</button>
                : <button className="btn btn-gold btn-sm" disabled={saving || !canPublish} onClick={doPublish}>{site.published ? "Republish ✨" : "Publish website ✨"}</button>}
            </div>
          </div>
        </div>
      </div>

      {/* ════════ RIGHT — live preview (desktop/tablet: sticky beside the form) ════════ */}
      {!isMobile && (
        <div className="ws-right">
          <div className="ws-pvlabel">Live preview <span>updates as you type</span></div>
          <div className="ws-phone">
            <div className="ws-phone-notch" />
            <div className="ws-phone-screen">
              <LivePreview site={site} crm={crm} />
            </div>
          </div>
          <div className="ws-pvfoot">Your real site — published at <b>{site.subdomain ? `${site.subdomain}.${EMBED_HOST}` : `yourname.${EMBED_HOST}`}</b></div>
        </div>
      )}

      {/* ════════ PHONE — the preview as a bottom sheet (the owner's phone IS the frame) ════════ */}
      {isMobile && pvOpen && (
        <>
          <div className="ws-pvsheet-backdrop" onClick={() => setPvOpen(false)} />
          <div className="ws-pvsheet" role="dialog" aria-label="Live preview">
            <div className="ws-pvsheet-head">
              <div>
                <div className="ws-pvlabel" style={{ marginBottom: 0 }}>Live preview <span>updates as you type</span></div>
                <div className="ws-pvsheet-url">{site.subdomain ? `${site.subdomain}.${EMBED_HOST}` : `yourname.${EMBED_HOST}`}</div>
              </div>
              <button type="button" className="ws-pvsheet-close" onClick={() => setPvOpen(false)} aria-label="Close preview">✕</button>
            </div>
            <div className="ws-pvsheet-body">
              <LivePreview site={site} crm={crm} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────── live preview (mini-mirror of the template) ──
function LivePreview({ site, crm = {} }) {
  const t = site.theme || {};
  const accent = t.accent || DEFAULT_THEME.accent;
  const base = t.base || DEFAULT_THEME.base;
  const areas = crm.areas || [];   // areas + pricing are LIVE from the CRM, not the row
  const town = areas[0]?.town || (areas[0]?.zip ? `the ${areas[0].zip} area` : "your area");
  const plans = (crm.plans || []).slice(0, 3);
  const reviews = site.reviews || [];
  const hasReviews = reviews.length > 0;
  const avg = hasReviews ? (reviews.reduce((s, r) => s + (Number(r.stars) || 5), 0) / reviews.length).toFixed(1) : null;
  const planNames = plans.map(p => p.name).filter(Boolean);
  const G = (v, ph) => v ? v : <span className="pv-ghost">{ph}</span>;

  // Mirrors the real template: prices are NEVER shown up front — the customer gets a
  // custom quote through the sign-up flow. So no "$/visit" and no pricing cards here.
  return (
    <div className="pv" style={{ "--pa": accent, "--pb": base }}>
      <div className="pv-head"><b>{site.business_name || <span className="pv-ghost">Your Business</span>}</b><span className="pv-cta">Get my price</span></div>
      <div className="pv-hero">
        {hasReviews
          ? <div className="pv-eyebrow">★ {avg} · Trusted by neighbors in {town}</div>
          : <div className="pv-eyebrow soft">Serving {town}</div>}
        <div className="pv-h1">{G(site.tagline, "Your headline goes here")}</div>
        <div className="pv-ticks"><span>✓ Insured &amp; bonded</span><span>✓ Photo when done</span><span>✓ No contracts</span></div>
        <div className="pv-quote">
          <div className="pv-qt">Start your service</div>
          <div className="pv-qnote">See your real price &amp; sign up in about 2 minutes — no charge today.</div>
          <div className="pv-field">Service address</div>
          <div className="pv-field">Zip code</div>
          <div className="pv-btn">See my price →</div>
        </div>
      </div>
      <div className="pv-trust">🛡️ Insured · 📷 Photo proof · ✅ Guaranteed · 🐾 Gate locked</div>
      <div className="pv-sec pv-qsec">
        <div className="pv-kicker">Your custom price</div>
        <div className="pv-qh">See your price in 30 seconds</div>
        <div className="pv-qlede">Every yard is different, so we price yours exactly — no guesswork, no sales call.</div>
        {planNames.length > 0 && <div className="pv-qplans">{planNames.join(" · ")} plans available</div>}
        <div className="pv-btn pv-btn-wide">See my price →</div>
      </div>
      {hasReviews && (
        <div className="pv-sec">
          <div className="pv-kicker">Loved by neighbors</div>
          {reviews.slice(0, 2).map((r, i) => (
            <div key={i} className="pv-review">
              <div className="pv-stars">{"★".repeat(Math.max(1, Math.round(Number(r.stars) || 5)))}</div>
              <div className="pv-rt">“{r.text || "Great service!"}”</div>
              <div className="pv-rn">{r.name || "Customer"}{r.town ? ` · ${r.town}` : ""}</div>
            </div>
          ))}
        </div>
      )}
      <div className="pv-foot">{site.guarantee_text || "Happy or it's free."}</div>
    </div>
  );
}

// ─────────────────────────── manage view (shown once published) ──
// After first publish the wizard steps give way to this: the live site shown inside a
// browser frame (the real LivePreview), one address bar with inline copy, balanced
// actions, and an "Edit site" door back into the steps.
function ManageView({ site, crm, liveUrl, copy, onEdit }) {
  const url = liveUrl || `https://${site.subdomain || "yourname"}.${EMBED_HOST}`;
  const bare = url.replace(/^https?:\/\//, "");
  return (
    <div className="ws-manage">
      <div className="ws-mhead">
        <div>
          <div className="ws-title">Your website</div>
          <div className="ws-sub">Live and ready to share.</div>
        </div>
        <span className="ws-status live">● Live</span>
      </div>

      {/* the live site, framed like a browser window */}
      <div className="ws-browser">
        <div className="ws-bbar">
          <span className="ws-bdots"><i /><i /><i /></span>
          <div className="ws-baddr">
            <span className="ws-block">🔒</span>
            <span className="ws-burl">{bare}</span>
          </div>
          <button className="btn btn-ghost btn-xs ws-bcopy" onClick={() => copy(url, "Link copied")}>📋 Copy</button>
        </div>
        <a className="ws-bview" href={url} target="_blank" rel="noreferrer" title="Open your live site">
          <div className="ws-bscreen"><LivePreview site={site} crm={crm} /></div>
          <span className="ws-bopen">↗ Open site</span>
        </a>
      </div>

      <div className="ws-mactions">
        <a className="btn btn-gold" href={url} target="_blank" rel="noreferrer">↗ View site</a>
        <button className="btn btn-ghost" onClick={() => copy(embedSnippet(site.id), "Embed snippet copied")}>＜＞ Copy embed</button>
      </div>

      <div className="ws-mtip">💡 Next: add this link to your Google Business Profile &amp; social bios.</div>

      <div className="ws-medit">
        <div className="ws-medit-txt">
          <div className="ws-medit-t">Need to make changes?</div>
          <div className="ws-medit-s">Edit your content, photos, pricing or theme — then republish. Nothing changes for visitors until you do.</div>
        </div>
        <button className="btn btn-ghost" onClick={onEdit}>✏️ Edit site</button>
      </div>
    </div>
  );
}

// ─────────────────────────── small building blocks ──
function Field({ label, children }) {
  return <div className="form-group" style={{ marginBottom: 12 }}><label>{label}</label>{children}</div>;
}
function Stars({ value, onChange }) {
  return (
    <div className="ws-stars">
      {[1, 2, 3, 4, 5].map(n => (
        <button key={n} type="button" className={n <= value ? "on" : ""} onClick={() => onChange(n)} title={`${n} star${n > 1 ? "s" : ""}`}>★</button>
      ))}
    </div>
  );
}
function ChecklistItem({ ok, label, onClick }) {
  return (
    <button type="button" className={`ws-clitem${ok ? " ok" : ""}`} onClick={onClick}>
      <span className="ws-clmark">{ok ? "✓" : "○"}</span>{label}
    </button>
  );
}
function Swatch({ label, value, onChange, warn }) {
  // relative luminance for a white-text contrast hint on the accent
  const lum = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
    if (!m) return 1;
    const c = [1, 2, 3].map(i => { const v = parseInt(m[i], 16) / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  };
  const lowContrast = warn && (1.05 / (lum(value) + 0.05)) < 3;
  return (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label>{label}</label>
      <div className="ws-swatchrow">
        <input type="color" value={value} onChange={e => onChange(e.target.value)} className="ws-colorinput" />
        <input value={value} onChange={e => onChange(e.target.value)} style={{ width: 110 }} />
        <div className="ws-presets">
          {ACCENT_PRESETS.map(c => <button key={c} type="button" className="ws-preset" style={{ background: c }} onClick={() => onChange(c)} title={c} />)}
        </div>
      </div>
      {lowContrast && <div className="ws-warn">⚠ Low contrast — white button text may be hard to read on this color.</div>}
    </div>
  );
}

function embedSnippet(siteId) {
  return `<div data-scooper-quote data-site="${siteId}"></div>\n<script async src="https://${EMBED_HOST}/embed.js"></script>`;
}

function ImageSlot({ label, url, busy, onFile, onClear }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {url ? (
        <div style={{ position: "relative" }}>
          <img className="ws-img" src={url} alt={label} />
          <button className="ws-x ws-x-float" onClick={onClear}>✕</button>
        </div>
      ) : (
        <label className="ws-drop">
          {busy ? "Uploading…" : "Click to upload"}
          <input type="file" accept="image/*" hidden onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
        </label>
      )}
    </div>
  );
}

// ─────────────────────────── styles (scoped by .ws-* names) ──
const WS_CSS = `
.ws-studio{display:flex;gap:26px;align-items:flex-start}
.ws-left{flex:1;min-width:0;max-width:640px}
.ws-right{position:sticky;top:84px;width:360px;flex-shrink:0}
@media (max-width:1040px){.ws-studio{flex-direction:column-reverse}.ws-left{max-width:none;width:100%}.ws-right{position:static;width:100%;max-width:380px;margin:0 auto}}

.ws-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;margin-bottom:18px}
.ws-title{font-size:21px;font-weight:600;letter-spacing:-.02em;color:var(--text)}
.ws-sub{font-size:12.5px;color:var(--text2);margin-top:3px;max-width:46ch;line-height:1.5}
.ws-status{font-size:11px;font-weight:600;padding:5px 11px;border-radius:999px;white-space:nowrap}
.ws-status.live{background:rgba(26,138,82,.12);color:var(--ok)}
.ws-status.draft{background:rgba(0,0,0,.05);color:var(--text2)}

.ws-steps{margin-bottom:20px}
.ws-stepbtn{background:none;border:none;cursor:pointer;padding:0;flex:1;display:flex;flex-direction:column;align-items:center}

.ws-form{background:var(--surface);border:1px solid var(--border);border-radius:var(--r-lg);box-shadow:var(--shadow);padding:22px 24px}
/* keep fields inside their column (grid children need min-width:0 or inputs overflow) */
.ws-form input,.ws-form select,.ws-form textarea{width:100%;box-sizing:border-box;min-width:0}
.ws-form .grid-2{gap:14px}
.ws-form .grid-2>*{min-width:0}
/* read-only "from your CRM" displays */
.ws-pulled{font-size:12px;color:var(--ok);background:rgba(26,138,82,.1);border:1px solid rgba(26,138,82,.2);border-radius:10px;padding:9px 12px;margin:0 0 14px;line-height:1.45}
.ws-roempty{font-size:12.5px;color:var(--text2);background:#fafafd;border:1px dashed var(--border);border-radius:10px;padding:12px 14px;line-height:1.5}
.ws-chips{display:flex;flex-wrap:wrap;gap:7px}
.ws-areachip{font-size:12px;font-weight:600;color:var(--text);background:var(--accent-weak);color:var(--accent);border-radius:999px;padding:5px 11px}
.ws-rolist{display:flex;flex-direction:column;gap:7px}
.ws-roitem{font-size:13px;color:var(--text);background:#fafafd;border:1px solid var(--border);border-radius:9px;padding:9px 12px}
.ws-roitem span{color:var(--text2)}
.ws-plansro{display:flex;gap:8px;flex-wrap:wrap}
.ws-planro{position:relative;flex:1;min-width:96px;text-align:center;border:1px solid var(--border);border-radius:12px;padding:14px 8px;background:var(--surface)}
.ws-planro.pop{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-weak)}
.ws-planro-badge{position:absolute;top:-9px;left:50%;transform:translateX(-50%);background:var(--accent);color:#fff;font-size:9.5px;font-weight:700;padding:2px 8px;border-radius:999px;white-space:nowrap}
.ws-planro-name{font-size:12px;font-weight:700;color:var(--text)}
.ws-planro-price{font-size:20px;font-weight:800;color:var(--text);margin-top:3px}
.ws-planro-price small{font-size:11px;font-weight:600;color:var(--text2)}
.ws-planro-mo{font-size:11px;color:var(--text2);margin-top:2px}
.ws-stephead{margin-bottom:16px}
.ws-steptitle{font-size:17px;font-weight:600;letter-spacing:-.015em;color:var(--text)}
.ws-stepblurb{font-size:12.5px;color:var(--text2);margin-top:2px}
.ws-sectitle{font-size:14px;font-weight:600;color:var(--text);margin-bottom:6px}
.ws-palette{display:grid;grid-template-columns:repeat(auto-fill,minmax(138px,1fr));gap:9px;margin-bottom:14px}
.ws-pal{position:relative;display:flex;align-items:center;gap:11px;padding:10px 12px;border:1.5px solid var(--border);border-radius:12px;background:var(--surface);cursor:pointer;text-align:left;transition:border-color .15s ease,box-shadow .15s ease}
.ws-pal:hover{border-color:var(--text3)}
.ws-pal.on{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-weak)}
.ws-pal-chips{display:inline-flex;flex-shrink:0}
.ws-pal-chips span{width:19px;height:19px;border-radius:50%;border:2px solid var(--surface);box-shadow:0 0 0 1px rgba(0,0,0,.06)}
.ws-pal-chips span:last-child{margin-left:-8px}
.ws-pal-name{font-size:12.5px;font-weight:600;color:var(--text)}
.ws-pal-check{position:absolute;top:6px;right:8px;font-size:11px;font-weight:800;color:var(--accent)}
.ws-custom{margin-top:4px}
.ws-custom>summary{font-size:13px;font-weight:600;color:var(--accent);cursor:pointer;list-style:none;display:inline-flex;align-items:center;gap:6px;user-select:none}
.ws-custom>summary::-webkit-details-marker{display:none}
.ws-custom>summary::before{content:"⊕";font-weight:700}
.ws-custom[open]>summary::before{content:"⊖"}
.ws-secrow{display:flex;align-items:center;justify-content:space-between;gap:8px}
.ws-opt{font-size:11px;color:var(--text3);font-weight:400}
.ws-fieldlabel{font-size:12px;font-weight:500;color:var(--text2);margin-bottom:6px}
.ws-hint{font-size:12px;color:var(--text2);background:var(--accent-weak);border-radius:9px;padding:8px 11px;margin:6px 0 12px;line-height:1.45}

.ws-crm{display:inline-flex;align-items:center;gap:4px;font-size:10.5px;font-weight:600;color:var(--ok);background:rgba(26,138,82,.1);padding:2px 8px;border-radius:999px;margin-left:6px;vertical-align:middle}

.ws-row{display:flex;gap:8px;align-items:center}
.ws-rowcard{border:1px solid var(--border);border-radius:11px;padding:11px 12px;margin-bottom:8px;transition:.18s var(--ease)}
.ws-rowcard.pop{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-weak)}
.ws-x{background:none;border:1px solid var(--border);color:var(--text2);width:30px;height:30px;border-radius:8px;cursor:pointer;flex-shrink:0;font-size:12px;transition:.15s var(--ease)}
.ws-x:hover{background:rgba(215,50,43,.08);color:var(--danger);border-color:rgba(215,50,43,.25)}
.ws-x-float{position:absolute;top:6px;right:6px;background:rgba(255,255,255,.92)}
.ws-money{display:flex;align-items:center;gap:3px;border:1px solid var(--border);border-radius:10px;padding:0 10px;background:var(--surface)}
.ws-money span{color:var(--text2)}
.ws-money input{border:none;width:64px;padding:9px 2px}.ws-money input:focus{box-shadow:none}
.ws-money em{color:var(--text3);font-style:normal;font-size:12px}
.ws-pop{margin-top:8px;font-size:12px;font-weight:600;border:1px solid var(--border);background:var(--surface);color:var(--text2);border-radius:8px;padding:6px 12px;cursor:pointer;transition:.15s var(--ease)}
.ws-pop:hover{border-color:var(--accent);color:var(--accent)}
.ws-pop.on{background:var(--accent);color:#fff;border-color:var(--accent)}

.ws-stars{display:inline-flex;gap:1px}
.ws-stars button{background:none;border:none;cursor:pointer;font-size:17px;line-height:1;color:var(--border);padding:0 1px;transition:.12s}
.ws-stars button.on{color:#F6B73C}

.ws-empty{text-align:center;border:1.5px dashed var(--border);border-radius:11px;padding:20px;color:var(--text3);display:flex;flex-direction:column;align-items:center;gap:10px;margin-bottom:8px;font-size:13px}

.ws-swatchrow{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.ws-colorinput{width:38px;height:34px;padding:0;border:1px solid var(--border);border-radius:9px;background:none;cursor:pointer}
.ws-presets{display:flex;gap:5px;margin-left:2px}
.ws-preset{width:22px;height:22px;border-radius:6px;border:1px solid rgba(0,0,0,.12);cursor:pointer;padding:0}
.ws-preset:hover{transform:scale(1.12)}
.ws-warn{font-size:11.5px;color:#9a6a00;background:#fff7e6;border:1px solid #ffe2a8;border-radius:8px;padding:6px 10px;margin-top:6px}

.ws-img{width:100%;height:120px;object-fit:cover;border-radius:10px;border:1px solid var(--border);background:#f6f8fa}
.ws-drop{border:1.5px dashed var(--border);border-radius:10px;padding:14px;text-align:center;font-size:12px;color:var(--text2);cursor:pointer;display:block}
.ws-drop:hover{border-color:var(--accent);color:var(--accent)}
.ws-gallery{display:grid;grid-template-columns:repeat(auto-fill,minmax(110px,1fr));gap:8px}
.ws-gitem{position:relative}
.ws-note{font-size:11.5px;color:var(--text3);margin-top:8px}

.ws-addr{display:flex;align-items:center;gap:6px}
.ws-addr input{flex:1}
.ws-addrhost{color:var(--text2);font-size:13px;white-space:nowrap}
.ws-substatus{font-size:12px;margin-top:6px;min-height:17px;color:var(--text2)}
.ws-substatus.available{color:var(--ok)}
.ws-substatus.taken,.ws-substatus.invalid,.ws-substatus.checkfailed{color:var(--danger)}

.ws-checklist{background:#fafafd;border:1px solid var(--border);border-radius:12px;padding:12px 14px;margin-top:14px}
.ws-cltitle{font-size:12px;font-weight:600;color:var(--text);margin-bottom:6px}
.ws-clitem{display:flex;align-items:center;gap:8px;width:100%;background:none;border:none;text-align:left;cursor:pointer;font-size:12.5px;color:var(--text2);padding:4px 0}
.ws-clitem:hover{color:var(--text)}
.ws-clitem.ok{color:var(--text)}
.ws-clmark{width:16px;text-align:center;color:var(--text3);font-weight:700}
.ws-clitem.ok .ws-clmark{color:var(--ok)}
.ws-liverow{margin-top:12px;font-size:13px}
.ws-liverow a{color:var(--accent);font-weight:600;text-decoration:none}

.ws-embed{background:#0f172a;color:#e2e8f0;padding:12px 14px;border-radius:10px;font-size:11px;overflow:auto;white-space:pre-wrap;margin-bottom:8px}
.ws-err{background:rgba(215,50,43,.08);border:1px solid rgba(215,50,43,.25);border-radius:9px;padding:9px 12px;font-size:12.5px;color:var(--danger);margin-top:12px}

.ws-foot{display:flex;align-items:center;gap:10px;margin-top:18px;border-top:1px solid var(--border);padding-top:16px;flex-wrap:wrap}
.ws-savestate{font-size:12px;color:var(--ok)}
.ws-savestate.busy{color:var(--text2)}

/* manage view (after publish) */
.ws-managewrap{max-width:600px;margin:0 auto;padding:6px 2px 8px}
.ws-mhead{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:16px}
.ws-browser{border:1px solid var(--border);border-radius:14px;overflow:hidden;box-shadow:var(--shadow);background:var(--surface)}
.ws-bbar{display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid var(--border);background:var(--bg)}
.ws-bdots{display:inline-flex;gap:5px;flex-shrink:0}
.ws-bdots i{width:9px;height:9px;border-radius:50%;background:var(--border)}
.ws-baddr{flex:1;display:flex;align-items:center;gap:7px;background:var(--surface);border:1px solid var(--border);border-radius:999px;padding:5px 13px;min-width:0}
.ws-block{font-size:10.5px;flex-shrink:0;opacity:.55}
.ws-burl{font-size:13px;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.ws-bcopy{flex-shrink:0}
.ws-bview{position:relative;display:block;text-decoration:none}
.ws-bscreen{height:300px;overflow:hidden;pointer-events:none;background:#fff}
.ws-bview::after{content:"";position:absolute;left:0;right:0;bottom:0;height:72px;background:linear-gradient(transparent,var(--surface));pointer-events:none}
.ws-bopen{position:absolute;right:13px;bottom:13px;z-index:2;background:var(--text);color:var(--surface);font-size:12px;font-weight:600;padding:7px 13px;border-radius:999px;opacity:0;transform:translateY(4px);transition:opacity .15s ease,transform .15s ease;box-shadow:var(--shadow)}
.ws-bview:hover .ws-bopen{opacity:1;transform:translateY(0)}
.ws-mactions{display:flex;gap:10px;margin-top:14px}
.ws-mactions>.btn{flex:1;text-align:center;justify-content:center}
.ws-mtip{font-size:12.5px;color:var(--text2);background:var(--bg);border:1px solid var(--border);border-radius:10px;padding:11px 13px;margin-top:14px;line-height:1.5}
.ws-medit{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-top:16px;border-top:1px solid var(--border);padding-top:18px}
.ws-medit-t{font-size:13.5px;font-weight:600;color:var(--text)}
.ws-medit-s{font-size:12.5px;color:var(--text2);margin-top:2px;line-height:1.45;max-width:44ch}

/* live preview phone */
.ws-pvlabel{font-size:12px;font-weight:600;color:var(--text);margin-bottom:10px;display:flex;align-items:center;gap:8px}
.ws-pvlabel span{font-weight:400;color:var(--text3);font-size:11px}
.ws-phone{background:#0c0c0e;border-radius:34px;padding:9px;box-shadow:var(--shadow-lg);position:relative}
.ws-phone-notch{position:absolute;top:9px;left:50%;transform:translateX(-50%);width:96px;height:18px;background:#0c0c0e;border-radius:0 0 12px 12px;z-index:3}
.ws-phone-screen{background:#fff;border-radius:26px;overflow:hidden;height:540px;overflow-y:auto;position:relative}
.ws-phone-screen::-webkit-scrollbar{width:0}
.ws-pvfoot{font-size:11px;color:var(--text3);text-align:center;margin-top:10px;line-height:1.5}
.ws-pvfoot b{color:var(--text2);font-weight:600}

/* preview content (mini landing page) */
.pv{font-size:11px;color:#15231b;line-height:1.5}
.pv-ghost{color:#b9c2bc;font-style:italic}
.pv-head{display:flex;align-items:center;justify-content:space-between;padding:11px 13px;border-bottom:1px solid #eef1ee;position:sticky;top:0;background:#fff;z-index:2}
.pv-head b{font-size:12.5px;font-weight:800}
.pv-cta{background:var(--pa);color:#fff;font-weight:700;font-size:9.5px;padding:5px 9px;border-radius:8px}
.pv-hero{padding:14px 13px 16px;background:radial-gradient(120% 90% at 90% -10%, color-mix(in srgb, var(--pb) 14%, #fff), #fff 60%)}
.pv-eyebrow{display:inline-block;font-size:9px;font-weight:700;color:var(--pb);background:color-mix(in srgb, var(--pb) 12%, #fff);padding:3px 8px;border-radius:999px;margin-bottom:9px}
.pv-eyebrow.soft{color:#5d6b63;background:#f1f4f1}
.pv-h1{font-size:18px;font-weight:800;letter-spacing:-.02em;line-height:1.08;margin-bottom:9px}
.pv-ticks{display:flex;flex-direction:column;gap:3px;font-size:10px;font-weight:600;color:#3c4b43;margin-bottom:12px}
.pv-quote{background:#fff;border:1px solid #e7ebe7;border-radius:14px;padding:12px;box-shadow:0 10px 26px rgba(20,40,30,.10)}
.pv-qt{font-weight:800;font-size:12px;margin-bottom:7px}
.pv-zip{border:1px solid #e0e5e0;border-radius:9px;padding:7px 9px;color:#b9c2bc;font-size:10px;margin-bottom:8px}
.pv-price{font-size:20px;font-weight:900;color:var(--pa);margin-bottom:8px}
.pv-price small{font-size:9px;font-weight:600;color:#8a958d;margin-left:2px}
.pv-btn{background:var(--pa);color:#fff;font-weight:800;text-align:center;padding:9px;border-radius:10px;font-size:11px}
.pv-trust{background:var(--pb);color:#fff;font-size:8.5px;font-weight:600;text-align:center;padding:7px 6px;line-height:1.6}
.pv-sec{padding:14px 13px;border-top:1px solid #f0f3f0}
.pv-kicker{font-size:8.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--pa);margin-bottom:9px}
.pv-qnote{font-size:9.5px;color:#7c857f;line-height:1.4;margin-bottom:9px}
.pv-field{border:1px solid #e0e5e0;border-radius:9px;padding:7px 9px;color:#b9c2bc;font-size:10px;margin-bottom:7px}
.pv-qsec{text-align:center}
.pv-qh{font-size:14px;font-weight:900;letter-spacing:-.01em;color:#15231b;margin:0 0 6px}
.pv-qlede{font-size:10px;color:#5d6b63;line-height:1.5;margin:0 auto 10px;max-width:30ch}
.pv-qplans{font-size:9.5px;font-weight:700;color:#15231b;margin-bottom:12px}
.pv-btn-wide{display:inline-block;padding-left:22px;padding-right:22px}
.pv-plans{display:flex;gap:6px}
.pv-plan{flex:1;border:1px solid #e7ebe7;border-radius:11px;padding:10px 6px;text-align:center;position:relative}
.pv-plan.pop{border-color:var(--pa);box-shadow:0 6px 16px color-mix(in srgb, var(--pa) 18%, transparent)}
.pv-badge{position:absolute;top:-7px;left:50%;transform:translateX(-50%);background:var(--pa);color:#fff;font-size:7px;font-weight:800;padding:2px 6px;border-radius:999px;white-space:nowrap}
.pv-pn{font-size:9.5px;font-weight:700;margin-bottom:3px}
.pv-pp{font-size:15px;font-weight:900}
.pv-pp small{font-size:8px;font-weight:600;color:#8a958d}
.pv-review{background:#fafbfa;border:1px solid #eef1ee;border-radius:10px;padding:9px;margin-bottom:6px}
.pv-stars{color:#F6B73C;font-size:10px;letter-spacing:1px}
.pv-rt{font-size:10px;margin:4px 0}
.pv-rn{font-size:9px;font-weight:700;color:#6a756d}
.pv-foot{background:color-mix(in srgb, var(--pb) 90%, #000);color:#fff;text-align:center;font-size:11px;font-weight:700;padding:16px 12px}

@media (prefers-reduced-motion:reduce){.ws-rowcard,.ws-x,.ws-pop,.ws-preset{transition:none}}

/* header right cluster (status pill + phone-only Preview button) */
.ws-headright{display:flex;flex-direction:column;align-items:flex-end;gap:8px;flex-shrink:0}

/* ── phones (≤768px): form first, thumb-sized targets, preview as a bottom sheet ── */
@media (max-width:768px){
  .ws-studio{flex-direction:column;gap:0}
  .ws-left{max-width:none;width:100%}
  .ws-form{padding:16px 14px}
  .ws-head{gap:10px}
  .ws-title{font-size:19px}
  .ws-pvbtn{white-space:nowrap}
  /* remove (✕) buttons and stars: 36px+ tap targets */
  .ws-x{width:36px;height:36px;font-size:13px}
  .ws-stars{gap:0}
  .ws-stars button{min-width:36px;min-height:36px;padding:6px 4px;font-size:22px}
  /* accent swatches: 36px, wrap instead of overflowing */
  .ws-presets{flex-wrap:wrap;gap:8px}
  .ws-preset{width:36px;height:36px;border-radius:9px}
  .ws-preset:hover{transform:none}
  .ws-colorinput{width:44px;height:40px}
  /* subdomain: input full width, host suffix drops under it */
  .ws-addr{flex-wrap:wrap}
  .ws-addr input{flex:1 1 100%}
  .ws-addrhost{white-space:normal;font-size:12px;word-break:break-all}
  /* manage view: no hover on touch — the "Open site" pill is always shown; the edit row stacks */
  .ws-bopen{opacity:1;transform:none}
  .ws-bscreen{height:220px}
  .ws-medit{flex-wrap:wrap}
  .ws-medit .btn{width:100%;justify-content:center}
  .ws-mactions{flex-wrap:wrap}
  .ws-mactions>.btn{flex:1 1 140px}
  .ws-managewrap{padding:0}
  .ws-embed{font-size:10.5px;word-break:break-all}
  .ws-checklist .ws-clitem{padding:8px 0;min-height:36px}
  /* the preview sheet */
  .ws-pvsheet-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.36);z-index:129}
  .ws-pvsheet{position:fixed;left:0;right:0;bottom:0;z-index:130;display:flex;flex-direction:column;min-height:0;max-height:85vh;max-height:85dvh;background:var(--surface);border-radius:var(--r-lg,16px) var(--r-lg,16px) 0 0;box-shadow:var(--shadow-lg);padding-bottom:env(safe-area-inset-bottom)}
  .ws-pvsheet-head{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 14px 10px;border-bottom:1px solid var(--border);flex-shrink:0}
  .ws-pvsheet-url{font-size:11.5px;color:var(--text2);margin-top:2px;word-break:break-all}
  .ws-pvsheet-close{width:40px;height:40px;border-radius:50%;border:1px solid var(--border);background:var(--bg);color:var(--text);font-size:15px;cursor:pointer;flex-shrink:0;display:flex;align-items:center;justify-content:center}
  .ws-pvsheet-body{flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;background:#fff}
}
/* touch devices above the phone breakpoint (tablets): hover-only affordance stays visible */
@media (hover:none){.ws-bopen{opacity:1;transform:none}}
`;
